// src/components/agent/ChatTopBar.js
//
// The 48px strip above the thread (frontend spec §8.10): sidebar-open button
// (mobile / collapsed rail), chat title (click → rename), "Agent is
// working…" pulse when a turn runs without a local stream, context chip,
// and a file-pane reopen tab for the mobile overlay.
import React, { useState } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import RenameDialog from '../assistant/RenameDialog';

const WorkingPulse = styled(AutoAwesomeIcon)({
  fontSize: 14,
  animation: 'workingPulse 1.6s ease-in-out infinite',
  '@keyframes workingPulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 1 },
  },
});

function ChatTopBar({
  chatName,
  hasChat,
  turnActive,
  streaming,
  contextPct,
  isMobile,
  sidebarCollapsed,
  onOpenSidebar,
  onRename,
  filePaneReopen, // { filename, onClick } | null
}) {
  const [renameOpen, setRenameOpen] = useState(false);
  const showWorking = turnActive && !streaming;

  return (
    <Box
      sx={{
        height: 48,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {(isMobile || sidebarCollapsed) && (
        <Tooltip title="Open chat list">
          <IconButton size="small" onClick={onOpenSidebar}>
            <MenuIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Typography
        variant="subtitle1"
        noWrap
        onClick={() => hasChat && setRenameOpen(true)}
        sx={{
          fontWeight: 600,
          cursor: hasChat ? 'pointer' : 'default',
          minWidth: 0,
          '&:hover': hasChat ? { color: 'primary.main' } : undefined,
        }}
      >
        {chatName || 'New chat'}
      </Typography>

      {showWorking && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <WorkingPulse />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            Agent is working…
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1 }} />

      {filePaneReopen && (
        <Chip
          icon={<DescriptionIcon />}
          label={filePaneReopen.filename}
          size="small"
          variant="outlined"
          onClick={filePaneReopen.onClick}
          sx={{ maxWidth: 180 }}
        />
      )}

      {contextPct !== null && contextPct !== undefined && (
        <Tooltip title="Context window used">
          <Chip
            label={`${contextPct}%`}
            size="small"
            variant="outlined"
            color={contextPct > 90 ? 'warning' : 'default'}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          />
        </Tooltip>
      )}

      {renameOpen && (
        <RenameDialog
          open
          onClose={() => setRenameOpen(false)}
          onSave={onRename}
          initialName={chatName || ''}
        />
      )}
    </Box>
  );
}

export default ChatTopBar;
